import * as React from "react";
import * as DTO from "@/interfaces";
import { useMemo, useState } from "react";
import { Download, Check } from "lucide-react";

interface OwnProps {
  videoProjectId: number;
  stickers: DTO.ComposedStickerResponse | undefined;
}

type Props = OwnProps;

const StickersFC: React.FC<Props> = ({ videoProjectId, stickers }) => {
  const [downloadedUris, setDownloadedUris] = useState<{ [uri: string]: boolean }>({});

  const downloadFile = async (filePath: string, fileName: string) => {
    try {
      const response = await fetch(filePath);
      if (!response.ok) throw new Error("Network response was not ok");

      const blob = await response.blob();
      const urlObject = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = urlObject;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloadedUris(prev => ({ ...prev, [filePath]: true }));

      setTimeout(() => {
        window.URL.revokeObjectURL(urlObject);
      }, 1500);
    } catch (error) {
      console.warn("CORS or network issue during blob download, falling back to direct download tab:", error);

      // Fallback: Open in a new tab so user can still download/view the sticker
      const link = document.createElement("a");
      link.href = filePath;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setDownloadedUris(prev => ({ ...prev, [filePath]: true }));
    }
  };

  const mediaList = useMemo(() => {
    const list: DTO.Media[] = [];
    stickers?.playerSticker?.stickers?.media?.forEach(m => list.push(m));
    stickers?.coachSticker?.stickers?.media?.forEach(m => list.push(m));
    return list;
  }, [stickers?.playerSticker?.stickers?.media, stickers?.coachSticker?.stickers?.media]);

  return (
    <div className="w-full flex flex-col text-left">
      <div className="flex flex-row items-center justify-between select-none">
        {mediaList.length === 0 && <span className="text-xs text-slate-400 font-medium">No Stickers</span>}
      </div>

      {mediaList.length > 0 && (
        <div className="flex flex-row flex-wrap gap-3.5 pt-1">
          {mediaList.map((media, index) => {
            const isDownloaded = !!downloadedUris[media.uri!!];
            return (
              <div
                key={`sticker_container_${index}`}
                className="relative group border border-slate-200 rounded-lg p-1.5 bg-slate-50/30 hover:bg-slate-50 hover:border-slate-300 hover:shadow-2xs transition-all w-22 h-24 flex items-center justify-center overflow-hidden"
              >
                <img
                  src={media?.thumbnailUri || media?.uri}
                  alt=""
                  className="max-w-full max-h-full object-contain rounded-md"
                />

                {isDownloaded && (
                  <div className="absolute inset-0 bg-emerald-50/10 flex items-center justify-center z-4">
                    <div className="bg-white p-1.5 rounded-full shadow-md flex items-center justify-center border border-emerald-100">
                      <Check className="size-4 text-emerald-600 animate-bounce" />
                    </div>
                  </div>
                )}

                <button
                  type="button"
                  className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer border-none outline-none z-10"
                  onClick={() =>
                    downloadFile(media.uri!!, `${videoProjectId}_STICKER_${media.category}_${index}.${media.extension}`)
                  }
                >
                  <div className="bg-white p-1.5 rounded-full shadow-md hover:scale-105 transition-transform duration-150 flex items-center justify-center">
                    {isDownloaded ? (
                      <Check className="size-4 text-emerald-600 animate-bounce" />
                    ) : (
                      <Download className="size-4 text-slate-700" />
                    )}
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const Stickers = StickersFC;
